import { useState } from 'react';
import { SimpleGrid, Text } from '../ui';
import { PlayerInput } from '../forms';
import { addInputs, isNotDefaultValue } from '../../helpers/math/spadesMath';
import { TeamInputHeading } from '../forms';
import { useValidateActuals } from '../../helpers/utils/hooks';
import {
  getActualsErrorText,
  hasAnyAutoGeneratedActuals,
} from '../../helpers/utils/helperFunctions';
import { ErrorModal, InputModal } from '../modals';

import type { Names, Round, InputValue, ModalOpenArgs } from '../../types';

interface ActualSectionProps {
  index: number;
  names: Names;
  isCurrent: boolean;
  roundHistory: Round[];
  currentRound: Round;
}

interface ModalState {
  isOpen: boolean;
  fieldToUpdate: string;
  playerName: string;
  type: 'Bid' | 'Actual';
  onCustomUpdate?: (value: InputValue) => void;
  inputId: string | undefined;
  dealerId: string | undefined;
}

function ActualSection({
  index,
  names,
  isCurrent,
  roundHistory,
  currentRound,
}: ActualSectionProps) {
  const { team1BidsAndActuals, team2BidsAndActuals } = currentRound;
  const [isValid, setIsValid] = useState(true);

  // Hoist modal state
  const [modalState, setModalState] = useState<ModalState>({
    isOpen: false,
    fieldToUpdate: '',
    playerName: '',
    type: 'Actual',
    onCustomUpdate: undefined,
    inputId: undefined,
    dealerId: undefined,
  });

  const handleOpenModal = ({
    fieldToUpdate,
    type,
    playerName,
    onCustomUpdate,
    inputId,
    dealerId,
  }: ModalOpenArgs) => {
    setModalState({
      isOpen: true,
      fieldToUpdate,
      playerName,
      type: type as 'Bid' | 'Actual',
      onCustomUpdate,
      inputId,
      dealerId,
    });
  };

  const handleCloseModal = (open: boolean) => {
    if (!open) {
      setModalState((prev) => ({ ...prev, isOpen: false }));
    }
  };

  // Get bids to determine if team total editing should be enabled
  const team1Bids = [team1BidsAndActuals?.p1Bid, team1BidsAndActuals?.p2Bid];
  const team2Bids = [team2BidsAndActuals?.p1Bid, team2BidsAndActuals?.p2Bid];

  // Each team's editability will be determined individually in TeamInputHeading
  const isEditable = true;

  const team1Actuals = {
    p1Actual: team1BidsAndActuals?.p1Actual ?? '',
    p2Actual: team1BidsAndActuals?.p2Actual ?? '',
  };
  const team2Actuals = {
    p1Actual: team2BidsAndActuals?.p1Actual ?? '',
    p2Actual: team2BidsAndActuals?.p2Actual ?? '',
  };

  // Handle team total display logic
  const getTeamTotalDisplay = (teamActuals: {
    p1Actual: InputValue;
    p2Actual: InputValue;
  }) => {
    const p1Actual = teamActuals.p1Actual;
    const p2Actual = teamActuals.p2Actual;

    // If both are empty, show 0
    if (p1Actual === '' && p2Actual === '') {
      return 0;
    }

    // Otherwise, calculate the actual total
    return addInputs(p1Actual, p2Actual);
  };

  const team1ActualTotal = getTeamTotalDisplay(team1Actuals);
  const team2ActualTotal = getTeamTotalDisplay(team2Actuals);

  const showAutoGeneratedKey = hasAnyAutoGeneratedActuals(currentRound);

  const roundActuals = [
    team1Actuals.p1Actual,
    team1Actuals.p2Actual,
    team2Actuals.p1Actual,
    team2Actuals.p2Actual,
  ];

  // Handle validation for team total values
  const isTeamTotalValue = (value: InputValue) => value === '';
  const allActualsAreSubmitted = roundActuals.every(
    (actual) => isNotDefaultValue(actual) && !isTeamTotalValue(actual),
  );

  // Calculate total for validation (only use numeric values)
  const getNumericTotal = (teamActuals: {
    p1Actual: InputValue;
    p2Actual: InputValue;
  }) => {
    const p1Actual = teamActuals.p1Actual;
    const p2Actual = teamActuals.p2Actual;

    // If using team total, return 0 for validation (will be handled separately)
    if (isTeamTotalValue(p1Actual) || isTeamTotalValue(p2Actual)) {
      return 0;
    }

    return addInputs(p1Actual, p2Actual);
  };

  const team1NumericTotal = getNumericTotal(team1Actuals);
  const team2NumericTotal = getNumericTotal(team2Actuals);
  const totalActuals = team1NumericTotal + team2NumericTotal;

  useValidateActuals(allActualsAreSubmitted, totalActuals, setIsValid);

  const errorMessage = getActualsErrorText(totalActuals);

  return (
    <div data-cy="actualSection" data-testid="actualSection">
      <InputModal
        isCurrent={isCurrent}
        playerName={modalState.playerName}
        isOpen={modalState.isOpen}
        setIsModalOpen={handleCloseModal}
        type={modalState.type}
        fieldToUpdate={modalState.fieldToUpdate}
        currentRound={currentRound}
        roundHistory={roundHistory}
        index={index}
        onCustomUpdate={modalState.onCustomUpdate}
      />

      <ErrorModal
        isOpen={!isValid && !modalState.isOpen}
        setIsModalOpen={setIsValid} // Note: This might need adjustment if we want to force it open
        index={index}
        names={names}
        isCurrent={isCurrent}
        roundHistory={roundHistory}
        currentRound={currentRound}
        errorMessage={errorMessage}
        onOpenModal={handleOpenModal}
      />
      <TeamInputHeading
        team1Total={team1ActualTotal}
        team2Total={team2ActualTotal}
        title="Actuals"
        team1Bids={team1Bids}
        team2Bids={team2Bids}
        isEditable={isEditable}
        index={index}
        isCurrent={isCurrent}
        currentRound={currentRound}
        roundHistory={roundHistory}
        onOpenModal={handleOpenModal}
      />
      <SimpleGrid columns={2} className="namesContainer" gap={2} mt={1.25}>
        <PlayerInput
          roundHistory={roundHistory}
          teamName={names.team1Name}
          index={index}
          isCurrent={isCurrent}
          type={'Actual'}
          playerName={names.t1p1Name}
          currentRound={currentRound}
          inputId="team1BidsAndActuals.p1Actual"
          dealerId="team1BidsAndActuals.p1Bid"
          fieldToUpdate={'team1BidsAndActuals.p1Actual'}
          playerInput={team1BidsAndActuals?.p1Actual}
          teamClassName="team1"
          onOpenModal={handleOpenModal}
        />
        <PlayerInput
          roundHistory={roundHistory}
          teamName={names.team2Name}
          index={index}
          isCurrent={isCurrent}
          currentRound={currentRound}
          inputId="team2BidsAndActuals.p1Actual"
          dealerId="team2BidsAndActuals.p1Bid"
          fieldToUpdate={'team2BidsAndActuals.p1Actual'}
          teamClassName="team2"
          playerName={names.t2p1Name}
          playerInput={team2BidsAndActuals?.p1Actual}
          type={'Actual'}
          onOpenModal={handleOpenModal}
        />
        <PlayerInput
          teamName={names.team1Name}
          roundHistory={roundHistory}
          index={index}
          isCurrent={isCurrent}
          currentRound={currentRound}
          playerName={names.t1p2Name}
          inputId="team1BidsAndActuals.p2Actual"
          dealerId="team1BidsAndActuals.p2Bid"
          fieldToUpdate={'team1BidsAndActuals.p2Actual'}
          playerInput={team1BidsAndActuals?.p2Actual}
          type={'Actual'}
          teamClassName="team1"
          onOpenModal={handleOpenModal}
        />
        <PlayerInput
          teamName={names.team2Name}
          roundHistory={roundHistory}
          index={index}
          isCurrent={isCurrent}
          currentRound={currentRound}
          playerName={names.t2p2Name}
          playerInput={team2BidsAndActuals?.p2Actual}
          type={'Actual'}
          inputId="team2BidsAndActuals.p2Actual"
          dealerId="team2BidsAndActuals.p2Bid"
          fieldToUpdate={'team2BidsAndActuals.p2Actual'}
          teamClassName="team2"
          onOpenModal={handleOpenModal}
        />
      </SimpleGrid>
      {showAutoGeneratedKey && (
        <Text mt={2.5} mb={0} textAlign="center" fontSize="xs" color="gray.500">
          * auto-generated
        </Text>
      )}
    </div>
  );
}

export default ActualSection;
